import React, {useEffect} from "react";
import {Grid, IconButton} from "@mui/material";
import {ArrowBack} from "@mui/icons-material";
import {RootGrid, HeaderTypography} from "./taxonomy";
import {PocChannelProperties} from "../../../../settings/default_properties";
import {getAttributes as utilGetAttributes, utilGetPartners, utilSearchLastImport} from "../../../../../utils/utils";
import EnhancedErrorDialog from "../../../../../components/EnhancedErrorDialog";
import ChannelModifier from "../components/channel_modifier";
import EnhancedCircularLoader from "../../../../../components/EnhancedCircularLoader";
import {loadProfiles} from "../../../../files/import_export_profiles";
import {formatProfileFromRemote} from "../../import/product/advanced_supplier_import";
import {pageContentGap} from "../../../../../theme";

interface ErrorState {
    open: boolean;
    errorHeader: string;
    errorMessage: string;
}

interface ChannelSettingsProps {
    setTitle?: (title: string, flag?: boolean) => void;
    retailers: any[];
    location: {
        key?: string;
    };
    history: {
        push: (path: string) => void;
        goBack: () => void;
    };
}

const iconButtonSx = (theme: any) => ({
    backgroundColor: "white",
    border: "none",
    borderRadius: "6px",
    "& svg": { color: "rgba(0, 0, 0, 0.54)" }
});
export default function ChannelSettings(props: ChannelSettingsProps) {
    const [error, setError] = React.useState<ErrorState>({open: false, errorHeader: "", errorMessage: ""});
    const [attributes, setAttributes] = React.useState<any[]>([]);
    const [groupedAttributes, setGroupedAttributes] = React.useState<{ [key: string]: any }>({});
    const [partners, setPartners] = React.useState<any[]>([]);
    const [profiles, setProfiles] = React.useState<any[] | undefined>(undefined);
    const [lastImports, setLastImports] = React.useState<{ [key: string]: any } | undefined>(undefined);
    const [loading, setLoading] = React.useState(true);
    useEffect(() => {
        const loadPartners = async () => {
            let partners = await utilGetPartners();
            if (partners.errorMessage) return;
            let sortedPartners = partners.sort((a: any, b: any) => a.CompanyName.localeCompare(b.CompanyName));
            setPartners(sortedPartners);
        }
        const getAttributes = async () => {
            const marketplaceRetailers = props.retailers.filter((retailer: any) => retailer.Marketplace === true);
            // Attributes only back the channel-specific settings; suppliers connected to
            // non-marketplace retailers alone have none to load.
            if (marketplaceRetailers.length === 0) return;
            const channelAttributes = marketplaceRetailers.map((retailer: any) => async () => {
                return await utilGetAttributes(retailer.Id, false);
            });
            let groupedAttributes: { [key: string]: any } = {};
            let attributes = await Promise.all(channelAttributes.map(async (attribute: any) => await attribute()));
            attributes = attributes.map((attribute: any, index: number) => {
                groupedAttributes[marketplaceRetailers[index].Id] = attribute;
                return attribute;
            });
            attributes = attributes.flat().filter((attribute: any, index: number, self: any[]) =>
                index === self.findIndex((t: any) => (
                    t.Name === attribute.Name
                ))
            );
            if (attributes.length === 0) {
                setError({
                    open: true,
                    errorHeader: "Error",
                    errorMessage: "Failed to load attributes"
                });
            } else {
                setGroupedAttributes(groupedAttributes);
                setAttributes(attributes);
            }
        }
        const loadImportProfiles = async () => {
            let profiles = await loadProfiles("ImportCatalogItems");
            setProfiles(profiles.map((profile: any) => formatProfileFromRemote(profile)));
        }
        const loadImports = async () => {
            const importsByChannel: { [key: string]: any } = {};
            // Every retailer gets a settings card, so every retailer needs its last import.
            const channels = props.retailers;
            const lastImports = await Promise.all(channels.map(async (channel: any) => {
                return await utilSearchLastImport(channel.Id);
            }));
            channels.forEach((channel: any, index: number) => {
                if(lastImports[index].success) {
                    let records = lastImports[index].data;
                    if(records.length > 0) {
                        importsByChannel[channel.Id] = records[0];
                    }
                }
            });
            setLastImports(importsByChannel);
        }
        const init = async () => {
            setLoading(true);
            await loadPartners();
            await getAttributes();
            await loadImportProfiles();
            await loadImports();
            setLoading(false);
        }
        init();
        // eslint-disable-next-line
    }, []);
    useEffect(() => {
        if (props.setTitle) {
            props.setTitle('POC: Channel Settings', true);
        }
    }, [props]);
    return (
        <RootGrid container
              style={{gap: pageContentGap, padding: "24px 14px 24px 14px", position: "relative"}}>
            <Grid container justifyContent={"space-between"} direction={"row"}>
                <Grid item container justifyContent={"flex-start"} alignItems={"center"} style={{gap: pageContentGap, width: "100%"}}>
                    <IconButton
                        sx={iconButtonSx}
                        style={{height: "36px", width: "36px", position: 'relative', zIndex: 1}}
                        onClick={(e) => {
                            e.preventDefault();
                            if (props.location.key === undefined)
                                props.history.push("/productonboardingcenter/catalog");
                            else
                                props.history.goBack();
                        }}
                        size="large">
                        <ArrowBack style={{height: "18px", width: "18px"}}/>
                    </IconButton>
                    <HeaderTypography>
                        Channel Settings
                    </HeaderTypography>
                </Grid>
            </Grid>
            {loading ? (
                <Grid container style={{width: "100%", height: "100%", justifyContent: "center", alignItems: "center"}}>
                    <EnhancedCircularLoader />
                </Grid>
            ) : (
                <>
                    <PocChannelProperties
                        options={attributes}
                        template={(templateProps: any) => {
                            return (
                                <ChannelModifier
                                    partners={partners}
                                    attributes={attributes}
                                    groupedAttributes={groupedAttributes}
                                    retailers={props.retailers}
                                    profiles={profiles}
                                    lastImports={lastImports}
                                    {...templateProps}
                                />
                            );
                        }}
                    />
                </>
            )}
            <EnhancedErrorDialog open={error.open} onClose={() => setError({...error, open: false})}
                                 errorHeader={error.errorHeader} errorMessage={error.errorMessage}/>
        </RootGrid>
    );
}
